/* ─────────────────────────────────────────────────────────────────────────
 * Properties.
 *
 * NAMES ARE REAL. BOUNDS ARE NOT. Read PLACEHOLDER_BOUNDS before using
 * anything in this file for anything that costs money.
 *
 * From the PRD: the properties are Blue Bell and DeSoto (patented claims) plus
 * surrounding unpatented lode claims on Forest Service ground, in Arizona.
 * Jurisdiction for the surrounding claims is itself an open PRD checklist item
 * — "Forest Service vs. BLM Hassayampa Field Office" is unresolved.
 *
 * The coordinates below trace back to kilroy-app-ui.html (34.5312°N,
 * 112.4108°W), which are INVENTED MOCKUP PLACEHOLDERS — plausible for the
 * Hassayampa region, not authoritative. No claim-boundary files exist yet.
 *
 * NOTHING MAY BE CUT FROM THESE BOUNDS. A pmtiles extract against a wrong
 * bounding box produces a 180MB pack of the wrong mountain, which the field
 * team discovers with no signal, forty minutes from the truck.
 *
 * When Brandon delivers real extents, the sourcing plan is:
 *   · BLM MLRS               → unpatented lode claims
 *   · county parcels         → patented Blue Bell / DeSoto
 *   · USFS admin boundaries  → jurisdiction
 *   · AZGS                   → geology
 *   · USGS topo              → contours + hillshade
 * ───────────────────────────────────────────────────────────────────────── */

/** Guard flag. Every surface that shows a boundary or offers a download must
 *  check this and say so out loud. */
export const PLACEHOLDER_BOUNDS = true;

export const PLACEHOLDER_NOTICE =
  'Boundaries are placeholders — real claim extents pending from Brandon. Offline packs cannot be cut yet.';

export type Jurisdiction = 'patented' | 'usfs' | 'blm' | 'unconfirmed';

export interface Property {
  id: string;
  name: string;
  /** PRD-grounded. */
  jurisdiction: Jurisdiction;
  /** PLACEHOLDER. [lon, lat]. */
  center: [number, number];
  /** PLACEHOLDER. [[west, south], [east, north]]. */
  bounds: [[number, number], [number, number]];
  /** Estimated pack size in MB. The server reports the real number once a pack
   *  is built; this is only for the download prompt's copy. */
  pack_mb: number;
  note?: string;
}

export const PROPERTIES: Property[] = [
  {
    id: 'blue-bell',
    name: 'Blue Bell',
    jurisdiction: 'patented',
    center: [-112.4108, 34.5312], // PLACEHOLDER — from the mockup
    bounds: [
      [-112.4708, 34.4812],
      [-112.3508, 34.5812],
    ],
    pack_mb: 180,
  },
  {
    id: 'desoto',
    name: 'DeSoto',
    jurisdiction: 'patented',
    center: [-112.3402, 34.4795], // PLACEHOLDER
    bounds: [
      [-112.4002, 34.4295],
      [-112.2802, 34.5295],
    ],
    pack_mb: 145,
  },
  {
    id: 'usfs-claims',
    name: 'Forest Service claims',
    jurisdiction: 'unconfirmed',
    center: [-112.3755, 34.5054], // PLACEHOLDER
    bounds: [
      [-112.4908, 34.4095],
      [-112.2602, 34.6012],
    ],
    pack_mb: 240,
    note: 'Jurisdiction unresolved in the PRD: USFS vs BLM Hassayampa Field Office.',
  },
];

export const propertyById = (id: string): Property =>
  PROPERTIES.find((p) => p.id === id) ?? PROPERTIES[0];

export const DEFAULT_PROPERTY = 'blue-bell';

/** Base layers named in the PRD. Sourcing is a server-side job. */
export const BASE_LAYERS = [
  { id: 'topo', label: 'USGS topo', status: 'planned' },
  { id: 'hillshade', label: 'Hillshade', status: 'planned' },
  { id: 'geology', label: 'AZGS geology', status: 'planned' },
  { id: 'aeromag', label: 'Aeromag / gravity', status: 'where available' },
  { id: 'drillholes', label: 'Existing drill holes', status: 'awaiting import' },
] as const;
